import { landingMotionInView } from "@/utils/landingMotion";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

type StepCardAlign = "left" | "center" | "right";

function StepCard({
    gradient = false,
    title,
    description,
    numSrc,
    align = "left",
    className = "",
}: {
    title: string;
    description: string;
    gradient?: boolean;
    numSrc: string;
    align?: StepCardAlign;
    className?: string;
}) {
    return (
        <div className={`relative ${className} md:max-w-[530px] font-nexa`}>
            <div className={`rounded-[22px] p-[1.5px] ${gradient ? "card-gradient shadow-[0_30px_90px_rgba(35,216,230,0.08)]" : "bg-black border border-white/35"}`}>
                <div className="relative rounded-[32px]  backdrop-blur-[18px] px-6 md:px-8 py-10">
                    <div className={`flex  ${align === "right" ? "justify-end" : "justify-start"}`}>
                        <img
                            src={numSrc}
                            alt="number icon"
                            height={60}
                            width={260}
                            className="pointer-events-none select-none h-auto object-contain"
                            loading="lazy"
                        />
                    </div>


                    <div className={`${align === "right" ? "text-right" : "text-left"} pt-2`}>
                        <h4 className="font-nexa text-2xl md:text-[32px] leading-tight font-bold">
                            {title}
                        </h4>
                        <p className="mt-2 text-base md:text-lg  text-white/70 ">
                            {description}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}



export default function HowItWorks() {
    const { t } = useTranslation();
    return (
        <section className="max-w-[1240px] w-full mx-auto py-10 md:py-20 px-4 xl:px-0">
            <div className="flex justify-center">
                {/* subtitle The lading prop firm */}
                <motion.div
                    className="w-fit h-auto p-[1.5px] rounded-[20px] bg-gradient-to-r from-[#1CCDE6] to-[#DBD633] flex justify-center items-center"
                    {...landingMotionInView}
                >
                    <div className="w-fit px-3 bg-black text-dark rounded-[20px] p-1 flex items-center justify-center">
                        <span
                            className="bg-clip-text text-[12px] sm:text-[13px] xl:text-base text-transparent font-nexa font-bold uppercase"
                            style={{
                                backgroundImage:
                                    "linear-gradient(to left, #D7D536, #20CDE2)",
                                display: "inline-block",
                            }}
                        >
                            {t("Process")}
                        </span>
                    </div>
                </motion.div>
            </div>
            <motion.h3
                className="text-[32px] font-nexa max-w-[890px] mx-auto text-center md:text-[56px] leading-tight font-normal pt-2 pb-5"
                {...landingMotionInView}
            >
                {t("How It Works")}  <span className="italic whitespace-nowrap md:whitespace-normal px-1 text-gradient font-light ">{t("Step By Step")}</span>
            </motion.h3>

            {/* Desktop / large layout (matches design) */}
            <div className="relative hidden md:block mt-10 w-full bg-[url('/image/funnel/backlines.png')] bg-cover bg-center bg-no-repeat">

                {/* fixed stage height so cards can be positioned */}
                <div className="relative w-full ">
                    {/* Card 01 (top-right) */}
                    <motion.div
                        className="relative flex justify-end"
                        {...landingMotionInView}
                    >
                        <StepCard
                            gradient={true}
                            title={t("Choose Your Account Size")}
                            description={t(
                                "Select the account that matches your trading goals and risk appetite. Flexible options designed for both beginners and experienced traders."
                            )}
                            numSrc="/image/funnel/num1.png"
                            align="left"
                        />
                    </motion.div>

                    {/* Card 02 (mid-left) */}
                    <motion.div
                        className="relative flex justify-start"
                        {...landingMotionInView}
                    >
                        <StepCard
                            title={t("Get Instant Access")}
                            description={t(
                                "Skip long approvals and complex evaluations. Your funded account is ready within minutes."
                            )}
                            numSrc="/image/funnel/num2.png"
                            align="right"
                            className="max-w-[460px]"
                        />
                    </motion.div>

                    {/* Card 03 (mid-right) */}
                    <motion.div
                        className="relative flex justify-end"
                        {...landingMotionInView}
                    >
                        <StepCard
                            title={t("Start Trading Immediately")}
                            description={t(
                                "Jump straight into the markets with real capital. No delays—trade forex, crypto, and more instantly."
                            )}
                            numSrc="/image/funnel/num3.png"
                            align="left"
                        />
                    </motion.div>

                    {/* Card 04 (bottom-left) */}
                    <motion.div
                        className="relative flex justify-start"
                        {...landingMotionInView}
                    >
                        <StepCard
                            title={t("Withdraw Your Profits")}
                            description={t(
                                "Keep what you earn with fast, hassle-free payouts. Withdraw profits easily and scale as you grow."
                            )}
                            numSrc="/image/funnel/num4.png"
                            align="right"
                            className="max-w-[470px]"
                        />
                    </motion.div>
                </div>
            </div>

            {/* Mobile layout (stacked) */}
            <div className="md:hidden mt-8 space-y-[70px] bg-[url('/image/funnel/backlinesmob.png')] bg-bottom bg-no-repeat bg-cover">
                <motion.div {...landingMotionInView}>
                    <StepCard
                        title={t("Choose Your Account Size")}
                        description={t(
                            "Select the account that matches your trading goals and risk appetite. Flexible options designed for both beginners and experienced traders."
                        )}
                        numSrc="/image/funnel/num1.png"
                        align="left"
                    />
                </motion.div>
                <motion.div {...landingMotionInView}>
                    <StepCard
                        title={t("Get Instant Access")}
                        description={t(
                            "Skip long approvals and complex evaluations. Your funded account is ready within minutes."
                        )}
                        numSrc="/image/funnel/num2.png"
                        align="right"
                    />
                </motion.div>
                <motion.div {...landingMotionInView}>
                    <StepCard
                        title={t("Start Trading Immediately")}
                        description={t(
                            "Jump straight into the markets with real capital. No delays—trade forex, crypto, and more instantly."
                        )}
                        numSrc="/image/funnel/num3.png"
                        align="left"
                    />
                </motion.div>
                <motion.div {...landingMotionInView}>
                    <StepCard
                        title={t("Withdraw Your Profits")}
                        description={t(
                            "Keep what you earn with fast, hassle-free payouts. Withdraw profits easily and scale as you grow."
                        )}
                        numSrc="/image/funnel/num4.png"
                        align="right"
                    />
                </motion.div>
            </div>
        </section>
    )
}